import type { AcademyState, Course } from '../types/academy'

export function lessonKey(slug: string, moduleIndex: number, lessonIndex: number): string {
  return `${slug}/${moduleIndex}-${lessonIndex}`
}

export function quizKey(slug: string, moduleIndex: number): string {
  return `${slug}/${moduleIndex}`
}

/** Lesson URLs read as /academy/learn/<course>/<module>-<lesson>, both 1 based. */
export function lessonPath(slug: string, moduleIndex: number, lessonIndex: number): string {
  return `/academy/learn/${slug}/${moduleIndex + 1}-${lessonIndex + 1}`
}

export function parseLessonParam(param: string | undefined): { moduleIndex: number; lessonIndex: number } | null {
  const match = /^(\d+)-(\d+)$/.exec(param ?? '')
  if (!match) return null
  return { moduleIndex: Number(match[1]) - 1, lessonIndex: Number(match[2]) - 1 }
}

export interface ModuleProgress {
  done: number
  total: number
  lessonsDone: boolean
  hasQuiz: boolean
  quizPassed: boolean
  complete: boolean
  percent: number
}

export function moduleProgress(course: Course, moduleIndex: number, state: AcademyState): ModuleProgress {
  const mod = course.modules[moduleIndex]
  const total = mod.lessons.length
  const done = mod.lessons.filter((_, li) => Boolean(state.completions[lessonKey(course.slug, moduleIndex, li)])).length
  const hasQuiz = Boolean(mod.quiz?.length)
  const quizPassed = hasQuiz ? Boolean(state.quizzes[quizKey(course.slug, moduleIndex)]?.passed) : true
  const lessonsDone = done === total
  return {
    done,
    total,
    lessonsDone,
    hasQuiz,
    quizPassed,
    complete: lessonsDone && quizPassed,
    percent: Math.round((done / total) * 100),
  }
}

export interface CourseProgress {
  completedModules: number
  totalModules: number
  completedLessons: number
  totalLessons: number
  percent: number
  started: boolean
  complete: boolean
}

export function courseProgress(course: Course, state: AcademyState): CourseProgress {
  const modules = course.modules.map((_, mi) => moduleProgress(course, mi, state))
  const completedModules = modules.filter((m) => m.complete).length
  const completedLessons = modules.reduce((sum, m) => sum + m.done, 0)
  const totalLessons = modules.reduce((sum, m) => sum + m.total, 0)
  return {
    completedModules,
    totalModules: modules.length,
    completedLessons,
    totalLessons,
    percent: Math.round((completedModules / modules.length) * 100),
    started: completedLessons > 0,
    complete: completedModules === modules.length,
  }
}

/** The first lesson not yet completed, or the final lesson once all are done. */
export function nextLesson(course: Course, state: AcademyState): { moduleIndex: number; lessonIndex: number } {
  for (let mi = 0; mi < course.modules.length; mi++) {
    for (let li = 0; li < course.modules[mi].lessons.length; li++) {
      if (!state.completions[lessonKey(course.slug, mi, li)]) return { moduleIndex: mi, lessonIndex: li }
    }
  }
  const lastModule = course.modules.length - 1
  return { moduleIndex: lastModule, lessonIndex: course.modules[lastModule].lessons.length - 1 }
}

export function isLessonUnlocked(moduleIndex: number, enrolled: boolean): boolean {
  return moduleIndex === 0 || enrolled
}

/** Consecutive days, ending today or yesterday, with at least one lesson completed. */
export function currentStreak(activityDays: string[]): number {
  const days = new Set(activityDays)
  const cursor = new Date()
  if (!days.has(dayStamp(cursor))) cursor.setDate(cursor.getDate() - 1)
  let streak = 0
  while (days.has(dayStamp(cursor))) {
    streak += 1
    cursor.setDate(cursor.getDate() - 1)
  }
  return streak
}

export function dayStamp(date: Date): string {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
}

export function totalLessons(course: Course): number {
  return course.modules.reduce((sum, m) => sum + m.lessons.length, 0)
}
